/**
 * Read: a page of printed music on the left, what a machine read off it on the right.
 *
 * Arranging begins with a score, and a great deal of music exists only as print. Reading
 * print is a machine's work and no machine does it without mistakes, so this board is where a
 * reader is looked at before it is trusted: its reading laid beside the page it came off,
 * page by page, played, and -- where the score the page was printed from is to hand --
 * counted against it note for note. Every reader is counted the same way
 * (`recognise/count.py`), so two of them are told apart by a number and not by an impression.
 *
 * Three things are asked: the scan, who reads it, and the true score if there is one. The
 * readers are the pipeline's (`GET /theory/readers`); one whose weights are not ours to ship
 * is offered here to be measured and nowhere else, and a scan opened on Arrange or the Lab
 * is read by the server's own reader, or Audiveris, whatever is chosen here.
 *
 * Nothing is arranged here. What a reader wrote is the scan as MusicXML, every movement of
 * it, to be saved, opened in the editor or handed to Arrange with one press, which opens it
 * there as a score given and asks who plays; a reading that may not be shipped is not handed.
 *
 * The scan stays in this browser, where pdf.js draws it; it is sent to the server afresh for
 * each reading, an upload being one job's and gone with it. Photos of the pages, picked
 * together, are made into that PDF on the server first, a page each in the order of their
 * names (`lib/pictures.ts`), and are a scan like any other from then on.
 */

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api/client";
import { useAsync } from "../api/useAsync";
import { Trouble } from "../components/Chrome";
import ReadDesks from "../components/ReadDesks";
import ReadRuns from "../components/ReadRuns";
import ReadToolbar, { ReadProgress } from "../components/ReadToolbar";
import { usePdf } from "../lib/pdf-pages";
import { useReadRuns } from "../lib/read-runs";
import { said } from "../lib/job-poll";
import { asPdf, called, isPage } from "../lib/pictures";

export default function ReadPage(): JSX.Element {
  const navigate = useNavigate();
  const known = useAsync("readers", () => api.readers());
  const readers = known.data?.readers ?? [];
  const [chosen, setChosen] = useState<string | null>(null);
  // The first reader this server can run, until one is chosen by hand.
  const reader = chosen ?? readers.find((one) => one.why === null)?.name ?? readers[0]?.name ?? "";

  const [scan, setScan] = useState<File | null>(null);
  const [refused, setRefused] = useState<string | null>(null);
  // Photos being made into the scan, by what they are called; a later pick drops them.
  const [binding, setBinding] = useState<string | null>(null);
  const picks = useRef(0);
  const [truth, setTruth] = useState<File | null>(null);
  const [page, setPage] = useState(1);
  const opened = usePdf(scan);
  const runs = useReadRuns(truth);
  const { clear } = runs;

  const open = (files: readonly File[]): void => {
    picks.current += 1;
    const mine = picks.current;
    const live = (): boolean => picks.current === mine;
    const name = called(files);
    setBinding(null);
    const opening = async (): Promise<void> => {
      const pages = await Promise.all(files.map(isPage));
      if (!live()) return;
      const odd = files[pages.indexOf(false)];
      if (odd !== undefined) {
        setRefused(
          `${odd.name} is not a PDF or a photo of a page. Read takes printed music as either.`,
        );
        return;
      }
      setRefused(null);
      setBinding(name);
      const pdf = await asPdf(files, live, () => undefined);
      if (pdf === null || !live()) return;
      setBinding(null);
      clear();
      setPage(1);
      setScan(pdf);
    };
    opening().catch((cause: unknown) => {
      if (!live()) return;
      setBinding(null);
      setRefused(`${name} could not be opened: ${said(cause)}`);
    });
  };

  // A reading put on the desk may have fewer pages than the page turned to has a number.
  const pages = opened.pdf?.numPages ?? null;
  useEffect(() => {
    if (pages !== null && page > pages) setPage(pages);
  }, [page, pages]);

  const by = readers.find((one) => one.name === reader);
  const shown = runs.shown;
  // A reading come onto the desk is shown at the first page it has bars of, where the page
  // turned to has none: a scan opens on its cover, and the reading is what was asked for. A
  // page still being read is left turned to, and is laid out there when it has been. Once:
  // what is read so far grows under a page turned to on purpose, and leaves it there.
  const arrived = useRef<number | null>(null);
  useEffect(() => {
    if (shown === null) arrived.current = null;
    const first = shown?.reading.spans?.[0]?.page;
    if (shown === null || first === undefined || arrived.current === shown.id) return;
    arrived.current = shown.id;
    const { spans, waiting } = shown.reading;
    setPage((now) =>
      spans?.some((one) => one.page === now) || waiting.includes(now) ? now : first,
    );
  }, [shown]);
  const span = shown?.reading.spans?.find((one) => one.page === page) ?? null;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <ReadToolbar
        scan={scan}
        binding={binding}
        pages={pages}
        readers={readers}
        reader={reader}
        truth={truth}
        reading={runs.busy !== null}
        onScan={open}
        onReader={setChosen}
        onTruth={setTruth}
        onRead={() => {
          if (scan !== null && by !== undefined) runs.read(scan, by);
        }}
      />
      {runs.busy === null ? null : (
        <ReadProgress progress={runs.busy.progress} onStop={runs.cancel} />
      )}
      {by === undefined || by.shipped || by.why !== null ? null : (
        <p className="shrink-0 border-b border-shell-edge bg-shell-bar px-2.5 py-1 text-[11px] text-shell-dim">
          {by.label} is here to be measured: {by.about} Nothing it reads is arranged.
        </p>
      )}
      {known.error === null ? null : (
        <Trouble>
          The pipeline did not answer when asked which readers it has: {known.error}
        </Trouble>
      )}
      {refused === null ? null : <Trouble>{refused}</Trouble>}
      {runs.error === null ? null : <Trouble>{runs.error}</Trouble>}

      {scan === null ? (
        <div className="flex min-h-0 flex-1 items-center justify-center p-8 text-center">
          <div className="max-w-md">
            <p className="text-[14px] font-medium text-shell-lit">
              A page of printed music on the left, what was read off it on the right.
            </p>
            <p className="mt-2 text-[12px] leading-relaxed text-shell-dim">
              Open a PDF or photos of its pages, pick who reads it, and give the true score if there
              is one: every reader is then counted the same way, note for note.
            </p>
          </div>
        </div>
      ) : (
        <>
          <ReadDesks
            name={scan.name}
            pdf={opened.pdf}
            unopened={opened.error}
            page={page}
            onPage={setPage}
            run={shown}
            counted={shown === null ? undefined : runs.counts[shown.id]}
            reading={runs.busy !== null}
            onOpenInEditor={(musicxml) => navigate("/editor", { state: { musicxml } })}
            onArrange={(run) =>
              navigate("/arrange", {
                state: { musicxml: run.reading.musicxml, name: scan.name, note: run.reading.note },
              })
            }
          />
          {runs.runs.length === 0 ? null : (
            <ReadRuns
              runs={runs.runs}
              shown={shown}
              counts={runs.counts}
              truth={truth}
              span={span}
              onShow={runs.show}
            />
          )}
        </>
      )}
    </div>
  );
}
